export const DESKTOP_AUTHORIZE_PATH = '/desktop/authorize'
export const isDesktopAuthorizePath = (path: string): boolean =>
  path === DESKTOP_AUTHORIZE_PATH

export interface DesktopAuthorizationRequest {
  client_id: string
  redirect_uri: string
  state: string
  code_challenge: string
  code_challenge_method: string
}

/** 回跳只允许客户端占用的 IPv4 loopback 端口，不能变成通用外链跳转。 */
export function parseDesktopAuthorization(
  search: string
): DesktopAuthorizationRequest | null {
  const params = new URLSearchParams(search)
  const keys = [
    'client_id',
    'redirect_uri',
    'state',
    'code_challenge',
    'code_challenge_method'
  ] as const
  if (keys.some((key) => params.getAll(key).length !== 1)) return null
  const request = Object.fromEntries(
    keys.map((key) => [key, params.get(key) || ''])
  ) as unknown as DesktopAuthorizationRequest
  const callback =
    /^http:\/\/127\.0\.0\.1:([1-9][0-9]{3,4})\/desktop\/login\/callback$/.exec(
      request.redirect_uri
    )
  if (
    !callback ||
    Number(callback[1]) < 1024 ||
    Number(callback[1]) > 65535 ||
    request.client_id !== 'todaywork-desktop' ||
    request.code_challenge_method !== 'S256' ||
    !/^[A-Za-z0-9_-]{43}$/.test(request.state) ||
    !/^[A-Za-z0-9_-]{43}$/.test(request.code_challenge)
  )
    return null
  return request
}

export function desktopAuthorizationCallback(
  request: DesktopAuthorizationRequest,
  code?: string
): string {
  if (
    !parseDesktopAuthorization(new URLSearchParams({ ...request }).toString())
  )
    throw new Error('授权链接无效')
  if (code !== undefined && !/^[A-Za-z0-9_-]{43}$/.test(code))
    throw new Error('授权响应无效，请重试')
  const target = new URL(request.redirect_uri)
  target.searchParams.set('state', request.state)
  target.searchParams.set(
    code === undefined ? 'error' : 'code',
    code === undefined ? 'access_denied' : code
  )
  return target.href
}

/** 微信绑定手机号仍在同一授权页完成，只清理微信 code，不丢失桌面端上下文。 */
export function desktopBindLoginURL(search: string, token: string): string {
  const params = new URLSearchParams(search)
  params.delete('code')
  params.set('token', token)
  params.set('bind', '1')
  return `${DESKTOP_AUTHORIZE_PATH}?${params.toString()}`
}

export async function authorizeDesktopAccount(
  request: DesktopAuthorizationRequest,
  userID: string
): Promise<string> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 15000)
  try {
    const response = await fetch(
      `${__API_SERVER_HOST__}/web/workgroup/desktop/login/authorize`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...request, expected_user_id: userID }),
        signal: controller.signal
      }
    )
    if (!response.ok) throw new Error('授权服务暂不可用，请稍后重试')
    const result = await response.json()
    if (result.code !== 200 || typeof result.data?.code !== 'string') {
      throw new Error(result.msg || '授权失败，请刷新页面重试')
    }
    return desktopAuthorizationCallback(request, result.data.code)
  } finally {
    clearTimeout(timer)
  }
}
